import { TableColumn, TableProps } from './TableTypes';
import { Button } from '../Button';
import { Size } from '../types';
import * as React from 'react';
import { Checkbox } from '../InputFields';
import { ButtonProps } from '../Button/Button';
import IconButton, { IconButtonProps } from '../Button/Iconbutton';
import { HyperlinkProps } from '../HyperLink/HyperLink';
import { HyperLink } from '../HyperLink';
import {
  StyledTableBody,
  StyledTableBodyRow,
  StyledTableCell,
  StyledTableCellContent,
  StyledTableCellIcon,
  StyledTableCellText,
  StyledTableHeaderColumn,
  StyledTableHeaderColumnContent,
  StyledTableHeaderColumns,
  StyledTableNoRowsLabelRow,
  StyledTable,
  StyledTableBodyWrapper,
  StyledTableHeaderRow,
  StyledTableHeaderColumnSortDirection,
  StyledTableCellNoteText,
  StyledTableCellTextWrapper,
} from './TableStyles';
import { defaultOnMouseDownHandler } from '../common';
import { SystemIcons } from '../icons';
import { useDimensionsRef } from 'rooks';
import styled from 'styled-components';

interface TableBodyProps extends TableProps {
  currentPageRows: any[];
  onRowClick?: (row: any) => void;
  selected?: any | any[];
  sortByColumn: (column: TableColumn) => void;
  onSelectAllClick: () => void;
  selectAllState: 'all' | 'some' | 'none';
}

const ColumnLabel = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const TableBody: React.FC<TableBodyProps> = (props) => {
  const [scrollContainerRef, dimensions, node] = useDimensionsRef({ updateOnResize: true });
  const [scrollable, setScrollable] = React.useState(false);

  React.useEffect(() => {
    const clientWidth = node?.clientWidth ?? 0;
    const scrollWidth = node?.scrollWidth ?? 0;
    setScrollable(scrollWidth > clientWidth);
  }, [dimensions, node, props.columns, props.rows]);

  const renderCellBody = (column: TableColumn, row: any) => {
    switch (column.type) {
      case 'link': {
        const tmp = {
          variant: 'default',
          href: '#',
          tabIndex: props.showLoadingIndicator ? -1 : 0,
          ...column.additionalProps,
        } as HyperlinkProps;
        return (
          <HyperLink
            style={column.colorFn && { color: column.colorFn(row, column.key) }}
            {...tmp}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              column.action && column.action(row, e);
            }}>
            {row[column.key]}
          </HyperLink>
        );
      }
      case 'icon': {
        const tmp = {
          iconColor: column.colorFn && column.colorFn(row, column.key),
          variant: 'secondary',
          shape: 'circular',
          tabIndex: props.showLoadingIndicator ? -1 : 0,
          useTransparentBackground: true,
          ...column.additionalProps,
        } as IconButtonProps;
        return (
          <IconButton
            {...tmp}
            action={(e) => {
              e?.stopPropagation();
              column.action && column.action(row, e);
            }}>
            {column.icon}
          </IconButton>
        );
      }
      case 'button': {
        const tmp = {
          variant: 'tertiary',
          size: Size.Small,
          tabIndex: props.showLoadingIndicator ? -1 : 0,
          ...column.additionalProps,
        } as ButtonProps;
        return (
          <Button
            style={column.colorFn && { color: column.colorFn(row, column.key) }}
            {...tmp}
            onClick={(e) => {
              e?.stopPropagation();
              column.action && column.action(row, e);
            }}>
            {row[column.key]}
          </Button>
        );
      }
      case 'boolean':
        return <Checkbox selected={!!row[column.key]} readOnly={true} />;
      case 'custom':
        return column.customContent && column.customContent(row, column.key);
      case 'number':
      case 'text':
      default:
        return (
          <>
            {column.icon && <StyledTableCellIcon>{column.icon}</StyledTableCellIcon>}
            <StyledTableCellTextWrapper textWrap={!column.shortenText}>
              <StyledTableCellText>{row[column.key]}</StyledTableCellText>
              {Boolean(column.noteKey) && Boolean(row[column.noteKey!]) && (
                <>
                  <StyledTableCellNoteText>{row[column.noteKey!]}</StyledTableCellNoteText>
                </>
              )}
            </StyledTableCellTextWrapper>
          </>
        );
    }
  };

  const isSelected = (row: any) => {
    return (
      (props.multiSelect && props.selected?.indexOf(props.keyExpr ? row[props.keyExpr] : row) > -1) ||
      (!props.multiSelect && props.selected === (props.keyExpr ? row[props.keyExpr] : row))
    );
  };

  return (
    <StyledTableBodyWrapper ref={scrollContainerRef as any} className={scrollable ? 'scrollable' : ''}>
      <StyledTable cellPadding="0" cellSpacing="0" data-testid="TestTable" className={`table ${props.layout || ''}`}>
        <StyledTableHeaderRow>
          <StyledTableHeaderColumns data-testid="TestTableColumnHeaderRow">
            {props.multiSelect && (
              <StyledTableHeaderColumn>
                <StyledTableHeaderColumnContent>
                  <Checkbox
                    selected={props.selectAllState === 'all'}
                    disabled={props.showLoadingIndicator}
                    semiSelected={props.selectAllState === 'some'}
                    select={props.onSelectAllClick}
                  />
                </StyledTableHeaderColumnContent>
              </StyledTableHeaderColumn>
            )}
            {props.columns.map((column: TableColumn) => (
              <StyledTableHeaderColumn
                key={column.key}
                onMouseDown={defaultOnMouseDownHandler}
                onClick={(e) => column.sortable && props.sortByColumn(column)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    column.sortable && props.sortByColumn(column);
                  }
                }}
                aria-pressed={column.key === props.sortProps?.column && !!props.sortProps?.direction ? true : undefined}
                aria-sort={column.key === props.sortProps?.column && !!props.sortProps?.direction ? (props.sortProps?.direction == 'asc' ? 'ascending' : 'descending') : undefined}
                style={{ width: column.width }}
                tabIndex={column.sortable && !props.showLoadingIndicator ? 0 : -1}
                className={`${!!column.sortable ? 'sortable' : ''} ${column.key === props.sortProps?.column && !!props.sortProps?.direction ? 'sorted' : ''} ${
                  column.justify || ''
                }`}>
                <StyledTableHeaderColumnContent>
                  <ColumnLabel>{column.name}</ColumnLabel>
                  {column.key === props.sortProps?.column && (
                    <StyledTableHeaderColumnSortDirection>
                      {props.sortProps?.direction === 'asc' ? <SystemIcons.ArrowLineUp size={'20px'} /> : <SystemIcons.ArrowLineDown size={'20px'} />}
                    </StyledTableHeaderColumnSortDirection>
                  )}
                </StyledTableHeaderColumnContent>
              </StyledTableHeaderColumn>
            ))}
          </StyledTableHeaderColumns>
        </StyledTableHeaderRow>
        <StyledTableBody>
          {props.currentPageRows.length > 0 ? (
            props.currentPageRows.map((row: any, index: number) => (
              <StyledTableBodyRow
                key={`row_${index}`}
                className={`${props.selectable ? 'selectable' : ''} ${isSelected(row) ? 'selected' : ''}`}
                onClick={() => props.onRowClick && props.onRowClick(row)}
                onMouseDown={defaultOnMouseDownHandler}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    props.onRowClick && props.onRowClick(row);
                  }
                }}
                tabIndex={props.selectable && !props.showLoadingIndicator ? 0 : undefined}
                data-testid="TestTableDataRow">
                {props.multiSelect && (
                  <StyledTableCell>
                    <StyledTableCellContent>
                      <Checkbox selected={isSelected(row)} />
                    </StyledTableCellContent>
                  </StyledTableCell>
                )}
                {props.columns.map((column: TableColumn) => (
                  <StyledTableCell key={`row_${index}_${column.key}`} style={{ maxWidth: column.width }} title={column.shortenText ? `${row[column.key]} \n ${column.noteKey ? row[column.noteKey!] : ''}` : ''}>
                    <StyledTableCellContent
                      withNote={Boolean(column.noteKey)}
                      className={`${column.shortenText ? `truncate-text` : ''} ${column.justify || ''}`}
                      style={{
                        color: column.colorFn && column.colorFn(row, column.key),
                      }}>
                      {renderCellBody(column, row)}
                    </StyledTableCellContent>
                  </StyledTableCell>
                ))}
              </StyledTableBodyRow>
            ))
          ) : (
            <StyledTableNoRowsLabelRow data-testid="TestTableNoDataRow">
              <StyledTableCell colSpan={props.columns.length + (props.multiSelect ? 1 : 0)}>
                <StyledTableCellContent className={'center'}>{props.noRowsLabel ?? 'There are no rows to display'}</StyledTableCellContent>
              </StyledTableCell>
            </StyledTableNoRowsLabelRow>
          )}
        </StyledTableBody>
      </StyledTable>
    </StyledTableBodyWrapper>
  );
};
export default TableBody;
